import { useState, useEffect, useCallback } from 'react';
import { tokens, Icon, Stack } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { PersistentBanners } from './PersistentBanners';

export interface Toast {
  id: string;
  severity: 'info' | 'warning' | 'success';
  message: string;
  action?: {
    label: string;
    onPress: () => void;
  };
  durationMs?: number;
}

export interface NotificationCenterProps {
  className?: string;
}

/**
 * NotificationCenter — GVW-136..143, 170..171.
 * 
 * Central hub for all notification surfaces: persistent banners, toasts, and
 * cost confirmation dialogs. Integrates with the platform notification bus.
 */
export function NotificationCenter({ className }: NotificationCenterProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeToast, setActiveToast] = useState<Toast | null>(null);
  const [dismissTimeout, setDismissTimeout] = useState<number | null>(null);

  // Toast management
  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const newToast: Toast = {
      id: `toast-${Date.now()}`,
      ...toast,
    };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
    if (activeToast?.id === toastId) {
      setActiveToast(null);
      if (dismissTimeout) {
        clearTimeout(dismissTimeout);
        setDismissTimeout(null);
      }
    }
  }, [activeToast, dismissTimeout]);

  // Process toast queue
  useEffect(() => {
    if (activeToast || toasts.length === 0) return;

    const nextToast = toasts[0];
    setActiveToast(nextToast);

    const duration = nextToast.durationMs ?? (nextToast.severity === 'warning' ? 8000 : 5000);
    const timeout = window.setTimeout(() => {
      dismissToast(nextToast.id);
    }, duration);
    setDismissTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [activeToast, toasts, dismissToast]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeout) clearTimeout(dismissTimeout);
    };
  }, [dismissTimeout]);

  // Expose toast API and test triggers to global scope for testing
  useEffect(() => {
    (window as any).geoviewer = {
      ...(window as any).geoviewer,
      notifications: {
        showToast,
        dismissToast,
      },
      testFixtures: {
        initNotificationStates: useViewerStore.getState().initTestNotificationStates,
        triggerOffline: useViewerStore.getState().triggerOfflineState,
        triggerStale: useViewerStore.getState().triggerStaleState,
        triggerRls: useViewerStore.getState().triggerRlsState,
        triggerCredentialsExpired: useViewerStore.getState().triggerCredentialsExpired,
        triggerQuotaExceeded: useViewerStore.getState().triggerQuotaExceeded,
        triggerCostWarning: useViewerStore.getState().triggerCostBudgetWarning,
        triggerCostReached: useViewerStore.getState().triggerCostBudgetReached,
      },
    };
  }, [showToast, dismissToast]);

  const toastContainer = activeToast ? (
    <div
      data-gvw-id="GVW-142"
      role={activeToast.severity === 'warning' ? 'alert' : 'status'}
      aria-live={activeToast.severity === 'warning' ? 'assertive' : 'polite'}
      style={{
        position: 'fixed',
        bottom: tokens.space[4],
        left: '50%',
        transform: 'translateX(-50%)',
        width: tokens.layout.popoverLg + 'px',
        maxWidth: `calc(100vw - ${tokens.space[5] ?? 32}px)`,
        zIndex: tokens.zIndex.modal + 10,
        borderRadius: tokens.radius.input,
        padding: `${tokens.space[3]} ${tokens.space[4]}`,
        background: (() => {
          switch (activeToast.severity) {
            case 'info': return 'var(--md-info-container)';
            case 'warning': return 'var(--md-warning-container)';
            case 'success': return 'var(--md-success-container)';
            default: return tokens.color.surface[2];
          }
        })(),
        color: (() => {
          switch (activeToast.severity) {
            case 'info': return 'var(--md-on-info-container)';
            case 'warning': return 'var(--md-on-warning-container)';
            case 'success': return 'var(--md-on-success-container)';
            default: return tokens.color.ink.primary;
          }
        })(),
        border: tokens.border.thin,
        boxShadow: `0 ${tokens.space[1]} ${tokens.space[2]} ${tokens.color.overlay.scrim}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.space[2],
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.size.sm,
      }}
    >
      <Icon
        name={(() => {
          switch (activeToast.severity) {
            case 'info': return 'info';
            case 'warning': return 'warning';
            case 'success': return 'check_circle';
            default: return 'info';
          }
        })()}
        size={20}
        decorative
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        {activeToast.message}
      </div>
      <Stack direction="row" gap={1}>
        {activeToast.action ? (
          <button
            type="button"
            onClick={activeToast.action.onPress}
            style={{
              minHeight: tokens.density.touchMin,
              border: 'none',
              borderRadius: tokens.radius.md,
              background: 'transparent',
              color: 'inherit',
              paddingInline: tokens.space[2],
              cursor: 'pointer',
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.medium,
            }}
          >
            {activeToast.action.label}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => dismissToast(activeToast.id)}
          aria-label="Dismiss notification"
          style={{
            minHeight: tokens.density.touchMin,
            width: tokens.density.touchMin,
            border: 'none',
            borderRadius: tokens.radius.md,
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="close" size={16} decorative />
        </button>
      </Stack>
    </div>
  ) : null;

  return (
    <div className={className}>
      <PersistentBanners />
      {toastContainer}
    </div>
  );
}